import React, { useMemo, useRef, useState } from 'react';

// Compressed equity-curve hero chart — PORTED VERBATIM from the Maghaweer Portfolio v2
// MiniTwrChart: inline-SVG area (portfolio) + dashed benchmark overlay, a sparse date axis,
// and a hover crosshair + tooltip so you can read both series at any date. Deliberately not a
// charting library: this is a summary glance. Both series arrive pre-rebased to the window
// (value 0 = window start) from tradingData.
//
// The interactive layer (crosshair, dots, tooltip) is HTML positioned by percent rather than
// SVG, because the chart uses preserveAspectRatio="none" (x is stretched to the container
// width) which would distort SVG circles into ellipses. The model / xOf / yOf math is kept
// exactly as the source.
//
// THE ONE CHANGE from source: colours come from GreenSkew's design token, not Maghaweer's
// hardcoded #c9a96e. The portfolio line/area use `rgb(var(--brand-gold-rgb))`, which is
// cobalt on the light default theme and gold in dark — so it themes correctly without a
// theme hook. The benchmark stays a fixed red. The P&L tooltip figures use 600/400 green/red
// so they read on the light background too.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtPct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(2)}%`);
const shortDate = (iso) => { const [, m, d] = iso.split('-'); return `${MONTHS[+m - 1]} ${+d}`; };

const ACCENT = 'rgb(var(--brand-gold-rgb))'; // portfolio: cobalt (light) / gold (dark), themed
const BENCH = '#d4696f';                      // benchmark (SPX): fixed red

export default function MiniTwrChart({ points, spyPoints, height = 120, legend = false }) {
  const wrapRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  const model = useMemo(() => {
    if (!points || points.length < 2) return null;
    const spyByDate = {};
    (spyPoints || []).forEach((p) => { spyByDate[p.date] = p.value; });
    const rows = points.map((p) => ({ date: p.date, port: p.value, spy: spyByDate[p.date] ?? null }));

    const W = 640;
    const H = height;
    const pad = 6;
    const vals = [];
    rows.forEach((r) => { vals.push(r.port); if (r.spy != null) vals.push(r.spy); });
    let lo = Math.min(...vals);
    let hi = Math.max(...vals);
    if (lo === hi) { lo -= 0.01; hi += 0.01; }

    const n = rows.length;
    const xOf = (i) => pad + (n <= 1 ? 0 : (i / (n - 1)) * (W - 2 * pad));
    const yOf = (v) => pad + (1 - (v - lo) / (hi - lo)) * (H - 2 * pad);

    const portLine = rows.map((r, i) => `${i ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(r.port).toFixed(1)}`).join(' ');
    const area = `${portLine} L${xOf(n - 1).toFixed(1)},${H} L${pad},${H} Z`;

    let spyLine = '';
    let started = false;
    rows.forEach((r, i) => {
      if (r.spy != null) { spyLine += `${started ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(r.spy).toFixed(1)} `; started = true; }
    });

    // ~5 evenly-spaced date ticks across the window
    const tickCount = Math.min(5, n);
    const ticks = tickCount <= 1
      ? [0]
      : Array.from(new Set(Array.from({ length: tickCount }, (_, k) => Math.round((k / (tickCount - 1)) * (n - 1)))));

    return { W, H, rows, n, xOf, yOf, portLine, area, spyLine: spyLine.trim(), ticks };
  }, [points, spyPoints, height]);

  if (!model) {
    return (
      <div className="flex items-center justify-center text-xs text-gray-400 italic" style={{ height }}>
        Not enough data to chart this window
      </div>
    );
  }

  const onMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const rx = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setHoverIdx(Math.round(rx * (model.n - 1)));
  };

  const hr = hoverIdx != null ? model.rows[hoverIdx] : null;
  const leftPct = hoverIdx != null ? (model.xOf(hoverIdx) / model.W) * 100 : 0;
  const yPct = (v) => (model.yOf(v) / model.H) * 100;
  const tipRight = leftPct > 62; // flip tooltip to the left near the right edge
  const clr = (v) => (v == null ? 'text-gray-400' : v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400');

  return (
    <div>
      {legend && (
        <div className="flex items-center gap-4 mb-1.5 text-[10px]">
          <span className="flex items-center gap-1.5"><span className="inline-block w-3.5 h-0.5 rounded" style={{ background: ACCENT }} /><span className="text-gray-500">Portfolio</span></span>
          {spyPoints && spyPoints.length ? (
            <span className="flex items-center gap-1.5"><span className="inline-block w-3.5 border-t border-dashed" style={{ borderColor: BENCH }} /><span className="text-gray-500">SPX</span></span>
          ) : null}
        </div>
      )}
      <div
        ref={wrapRef}
        className="relative"
        style={{ height }}
        onMouseMove={onMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <svg viewBox={`0 0 ${model.W} ${model.H}`} preserveAspectRatio="none" className="w-full h-full block">
          <defs>
            {/* stop-color / stroke are set via the CSS `style` property, not the SVG
                presentation attribute — var() resolves reliably only in a real CSS property
                (iOS Safari does not substitute var() in a presentation attribute). */}
            <linearGradient id="tradingHeroFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" style={{ stopColor: ACCENT }} stopOpacity="0.26" />
              <stop offset="1" style={{ stopColor: ACCENT }} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={model.area} fill="url(#tradingHeroFill)" />
          {model.spyLine && <path d={model.spyLine} fill="none" stroke={BENCH} strokeWidth="1.3" strokeDasharray="5 4" opacity="0.8" />}
          <path d={model.portLine} fill="none" style={{ stroke: ACCENT }} strokeWidth="2" />
        </svg>

        {/* hover crosshair + dots + tooltip */}
        {hr && (
          <>
            <span className="absolute top-0 bottom-0 w-px bg-gray-400/40 pointer-events-none" style={{ left: `${leftPct}%` }} />
            <span className="absolute w-2 h-2 rounded-full -translate-x-1/2 -translate-y-1/2 ring-2 ring-white/20 pointer-events-none"
              style={{ background: ACCENT, left: `${leftPct}%`, top: `${yPct(hr.port)}%` }} />
            {hr.spy != null && (
              <span className="absolute w-2 h-2 rounded-full -translate-x-1/2 -translate-y-1/2 ring-2 ring-white/20 pointer-events-none"
                style={{ background: BENCH, left: `${leftPct}%`, top: `${yPct(hr.spy)}%` }} />
            )}
            <div
              className="absolute top-1 z-10 pointer-events-none rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-[#0d1117]/95 backdrop-blur px-2.5 py-1.5 shadow-lg"
              style={{ left: `${leftPct}%`, transform: tipRight ? 'translateX(-100%) translateX(-8px)' : 'translateX(8px)' }}
            >
              <div className="text-[10px] text-gray-400 font-mono mb-0.5">{hr.date}</div>
              <div className="flex items-center gap-1.5 text-[11px] whitespace-nowrap">
                <span className="inline-block w-2 h-0.5" style={{ background: ACCENT }} />
                <span className="text-gray-500">Portfolio</span>
                <span className={`ml-auto font-mono tabular-nums ${clr(hr.port)}`}>{fmtPct(hr.port)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] whitespace-nowrap">
                <span className="inline-block w-2 h-0.5" style={{ background: BENCH }} />
                <span className="text-gray-500">SPX</span>
                <span className={`ml-auto font-mono tabular-nums ${clr(hr.spy)}`}>{fmtPct(hr.spy)}</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* sparse date axis */}
      <div className="relative h-4 mt-1">
        {model.ticks.map((i, k) => {
          const pct = (model.xOf(i) / model.W) * 100;
          const isFirst = k === 0;
          const isLast = k === model.ticks.length - 1;
          const style = isFirst ? { left: 0 } : isLast ? { right: 0 } : { left: `${pct}%`, transform: 'translateX(-50%)' };
          return (
            <span key={i} className="absolute text-[9px] font-mono text-gray-400" style={style}>
              {shortDate(model.rows[i].date)}
            </span>
          );
        })}
      </div>
    </div>
  );
}
